import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { keyframes } from '@mui/material/styles'
import { useQueryClient } from '@tanstack/react-query'

import { queryKeys } from '@/api/query-keys'
import { useAdminDashboardMetrics } from '@/features/dashboard/api/admin-dashboard-queries'
import { useCampaigns } from '@/features/campaigns/api/campaign-queries'
import { formatInstrumentDashboardLastUpdated } from '@/features/campaigns/lib/insight-display'
import { useSessionStore } from '@/stores/useSessionStore'
import { useToast } from '@/shared/toast/ToastProvider'
import { RiRefreshLine } from '@/shared/icons/remix'

const refreshSpin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const REFRESH_ERROR_MESSAGE = 'Unable to refresh. Please try again.'

function isAdminDashboardPath(pathname: string): boolean {
  return pathname === '/' || pathname === '/dashboard'
}

export function useShowAdminDashboardStatus(pathname: string, role: string | undefined): boolean {
  return role === 'ADMIN' && isAdminDashboardPath(pathname)
}

export function AppShellAdminDashboardStatus() {
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const tenantKey = tenantId ?? '_'

  const {
    isFetching: metricsFetching,
    refetch: refetchMetrics,
    dataUpdatedAt: metricsUpdatedAt,
  } = useAdminDashboardMetrics()
  const {
    isFetching: campaignsFetching,
    refetch: refetchCampaigns,
    dataUpdatedAt: campaignsUpdatedAt,
  } = useCampaigns({ refetchIntervalMs: 30_000 })

  const isRefreshing = metricsFetching || campaignsFetching
  const lastUpdatedIso = new Date(
    Math.max(metricsUpdatedAt, campaignsUpdatedAt),
  ).toISOString()

  const onRefresh = async () => {
    const [metricsResult, campaignsResult] = await Promise.all([
      refetchMetrics(),
      refetchCampaigns(),
    ])
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.adminDashboard.metrics(tenantKey) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.campaigns.all(tenantKey) }),
    ])
    if (metricsResult.isError || campaignsResult.isError) {
      showToast(REFRESH_ERROR_MESSAGE, 'error')
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexShrink: 0,
      }}
    >
      <Typography
        data-testid="admin-dashboard-last-updated"
        sx={{
          fontFamily: '"Inter", sans-serif',
          fontSize: '14px',
          lineHeight: '20px',
          letterSpacing: '-0.1504px',
          color: '#4A5565',
          whiteSpace: 'nowrap',
        }}
      >
        Last Update {formatInstrumentDashboardLastUpdated(lastUpdatedIso)}
      </Typography>
      <IconButton
        size="small"
        aria-label={isRefreshing ? 'Refreshing dashboard' : 'Refresh dashboard'}
        aria-busy={isRefreshing}
        data-testid="admin-dashboard-refresh"
        disabled={isRefreshing}
        onClick={() => void onRefresh()}
        sx={{
          width: 36,
          height: 36,
          color: '#4A5565',
          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
        }}
      >
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            animation: isRefreshing ? `${refreshSpin} 1s linear infinite` : 'none',
          }}
        >
          <RiRefreshLine size={20} />
        </Box>
      </IconButton>
    </Box>
  )
}
